// Money › Receipts: the rows the page reads (receipts through RLS, the same rows the list policy gives),
// and the small rules the list and the drawer share (prototype 30-receipts.js rStatus, receiptRows; spec 08 R-20, R-21).

export type Kind = 'cash' | 'cheque' | 'transfer' | 'card';
export type Receipt = {
  id: string; display_number: string; status: 'issued' | 'voided' | 'deleted_by_retention'; issued_at: string; total: number; currency: string;
  customer_id: string | null; created_by: string; branch_id: string; narrative: string | null; refs: string[]; signer_name: string | null;
  captured_offline: boolean; synced_at: string | null; review_state: string; voided_at: string | null; voided_by: string | null; void_reason: string | null;
  public_id: string | null; location: { lat: number; lng: number } | null;
};
export type Line = { id: string; receipt_id: string; kind: Kind; amount: number; position: number; cheque_no: string | null; bank_code: string | null; cheque_date: string | null; transfer_ref: string | null; card_last4: string | null };
export type Doc = { receipt_id: string; kind: 'final' | 'void'; pages: number; bytes: number; template_id: string; rendered_at: string };
export type Mail = { id: number; kind: 'receipt' | 'resend' | 'sent_to' | 'void'; to: string[]; cc: string[]; state: 'waiting_pdf' | 'queued' | 'sending' | 'sent' | 'failed' | 'bounced'; pdf_problem: boolean; created_at: string; sent_at: string | null };

export type Filter = { by?: string; branch?: string; kind?: string; status?: '' | 'issued' | 'voided' | 'attention'; from?: string; to?: string };

/** Search matches the number, customer, collector, cheque numbers, references and narrative (R-21). */
export function matches(r: Receipt, lines: Line[], q: string, names: { cust: (id: string | null) => string; person: (id: string) => string }) {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  const hay = [r.display_number, names.cust(r.customer_id), names.person(r.created_by), r.narrative || '', ...(r.refs || []),
    ...lines.map(l => [l.cheque_no, l.transfer_ref, l.card_last4].filter(Boolean).join(' '))].join(' ').toLowerCase();
  return s.split(/\s+/).every(w => hay.includes(w));
}

/** The Dubai calendar day of a time (UTC+4, no daylight saving). */
export const dubaiDay = (iso: string) => new Date(Date.parse(iso) + 4 * 3_600_000).toISOString().slice(0, 10);
export function inFilter(r: Receipt, lines: Line[], f: Filter) {
  if (f.by && r.created_by !== f.by) return false;
  if (f.branch && r.branch_id !== f.branch) return false;
  if (f.kind && !lines.some(l => l.kind === f.kind)) return false;
  if (f.status === 'issued' && r.status !== 'issued') return false;
  if (f.status === 'voided' && r.status !== 'voided') return false;
  if (f.status === 'attention' && !(r.status === 'issued' && r.review_state === 'open')) return false;
  if (f.from && dubaiDay(r.issued_at) < f.from) return false;
  if (f.to && dubaiDay(r.issued_at) > f.to) return false;
  return true;
}

/** The sum bar: N receipts and their total, leaving out voided ones. */
export const sumOf = (rs: Receipt[]) => rs.filter(r => r.status !== 'voided').reduce((a, r) => a + Number(r.total), 0);

/** Payment kinds of a receipt, each with how many lines (the "Paid by" icons). */
export function paidBy(lines: Line[]): [Kind, number][] {
  const order: Kind[] = ['cash', 'cheque', 'transfer', 'card'];
  return order.map(k => [k, lines.filter(l => l.kind === k).length] as [Kind, number]).filter(([, n]) => n > 0);
}

/** The verify page link printed on the receipt's QR (spec 06 R-46). */
export const verifyLink = (r: Pick<Receipt, 'public_id'>) => (r.public_id ? 'https://verify.xreceived.com/r/' + r.public_id : null);

/** The file the receipt's PDF card offers: the VOID copy once voided and stored, else the receipt. */
export function docFor(r: Pick<Receipt, 'id' | 'status'>, docs: Doc[]) {
  const mine = docs.filter(d => d.receipt_id === r.id);
  return (r.status === 'voided' && mine.find(d => d.kind === 'void')) || mine.find(d => d.kind === 'final') || null;
}

/** What the drawer says about the customer email (prototype mailState; spec 06 R-56). */
export function mailWord(m: Mail | undefined): { tone: 'good' | 'warn' | 'bad' | 'mute'; key: 'none' | 'waiting' | 'problem' | 'sending' | 'sent' | 'failed' } {
  if (!m) return { tone: 'mute', key: 'none' };
  if (m.state === 'waiting_pdf') return m.pdf_problem ? { tone: 'bad', key: 'problem' } : { tone: 'warn', key: 'waiting' };
  if (m.state === 'sent') return { tone: 'good', key: 'sent' };
  if (m.state === 'failed' || m.state === 'bounced') return { tone: 'bad', key: 'failed' };
  return { tone: 'warn', key: 'sending' };
}
